/**
 * ============================================================================
 * IngredientPopupManager — CocktailConnexion (Complet v2.3 corrigé)
 * ============================================================================
 */
class IngredientPopupManager {
  constructor() {
    this.overlay = null;
    this.popup = null;
    this.closeBtn = null;
    this.startTime = 0;
    this.currentType = null;
    this.trigger = null;
    this.data = {};
    this.init();
  }

  /* -------------------------------------------------------------------------- */
  /*  INIT                                                                      */
  /* -------------------------------------------------------------------------- */
  init() {
    const triggers = document.querySelectorAll(
      '.ingredient-popup-trigger, .ingredient[data-link], .ingredient[data-internal-link]'
    );

    triggers.forEach(t => {
      t.style.cursor = 'pointer';
      t.addEventListener('click', (e) => this.openPopup(e, t));
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closePopup();
    });
  }

  /* -------------------------------------------------------------------------- */
  /*  HELPERS — Parsing & Typage                                                */
  /* -------------------------------------------------------------------------- */
  parseData(el) {
    return {
      name: el.dataset.name || 'Ingrédient',
      id: el.dataset.id || '',
      category: el.dataset.category || 'produit',
      headline: el.dataset.headline || '',
      price: el.dataset.price || '',
      rating: parseFloat(el.dataset.rating || '0'),
      reviews: parseInt(el.dataset.reviews || '0', 10),
      image: el.dataset.image || '',
      description: el.dataset.description || '',
      link: el.dataset.link || '',
      internalLink: el.dataset.internalLink || '',
    };
  }

  isInternal(data) {
    if (data.internalLink) return true;
    if (data.link && data.link.includes('cocktailconnexion.com')) return true;
    return false;
  }

  isAmazon(data) {
    if (data.internalLink) return false;
    if (data.link && (data.link.includes('amazon') || data.link.includes('amzn'))) return true;
    return false;
  }

  escapeHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Rendu des étoiles avec support demi-étoile
   */
  buildStars(rating) {
    const r = Math.round(rating * 2) / 2;
    let html = '<span class="popup-stars">';
    for (let i = 1; i <= 5; i++) {
      if (r >= i) {
        html += '<span class="star-full">★</span>';
      } else if (r >= i - 0.5) {
        html += '<span class="star-half">★</span>';
      } else {
        html += '<span class="star-empty">★</span>';
      }
    }
    html += '</span>';
    return html;
  }

  /**
   * Transforme la description en bloc "Pourquoi ce produit ?" + liste ✅
   */
  buildBenefits(desc) {
    if (!desc) return '';
    if (/<li>/i.test(desc)) {
      let clean = desc.replace(/<\/?ul[^>]*>/gi, '').replace(/<\/?ol[^>]*>/gi, '');
      return `<h3 class="popup-benefits-title">Pourquoi ce produit ?</h3>\n<ul class="popup-benefits">${clean}</ul>`;
    }
    const items = desc.split(/\n/).map(s => s.trim()).filter(Boolean);
    if (!items.length) return '';
    const lis = items.map(item => `<li>${this.escapeHtml(item)}</li>`).join('');
    return `<h3 class="popup-benefits-title">Pourquoi ce produit ?</h3>\n<ul class="popup-benefits">${lis}</ul>`;
  }

  /* -------------------------------------------------------------------------- */
  /*  TEMPLATES HTML                                                            */
  /* -------------------------------------------------------------------------- */

  /** Template A — Interne (Spiritueux / Liqueurs / Vins) */
  buildInternalHTML(data) {
    const headline = data.headline
      ? this.escapeHtml(data.headline)
      : `🍸 ${data.category ? data.category.toUpperCase() : 'NOTRE'} : Notre Sélection Expert`;

    let argsHtml = data.description;
    if (argsHtml && !argsHtml.trim().startsWith('<')) {
      argsHtml = `<li>${argsHtml.replace(/\n/g, '</li><li>')}</li>`;
    }

    return `
      <button class="popup-close" aria-label="Fermer">&times;</button>

      <h2 class="popup-title popup-title-internal">${this.escapeHtml(data.name)}</h2>

      <div class="popup-headline">${headline}</div>

      <div class="popup-internal-intro">
        <p>Vous cherchez <strong>${this.escapeHtml(data.name)}</strong> parfait pour votre Cocktail ?</p>
        <p>Sur cette page, nous avons sélectionné les meilleurs ${this.escapeHtml(data.category || 'produits')} en fonction de :</p>
      </div>

      <ul class="popup-internal-args">
        ${argsHtml || `
          <li>🔍 Votre cocktail : Mojito, un Mai Tai, Dark 'n' Stormy ..</li>
          <li>💰 Votre budget : de 20€ à 150€</li>
          <li>🚀 Livraison : sous 24/48h chez nos partenaires</li>
          <li>⭐ Notre expertise : testés et approuvés par nos mixologues</li>
        `}
      </ul>

      <a href="${this.escapeHtml(data.internalLink || data.link)}" 
         class="popup-cta popup-cta-internal">
        Découvrir notre sélection <span class="popup-arrow">→</span>
      </a>
    `;
  }

  /** 
   * Template B — Amazon (Ingrédients avec prix, avis, etc.)
   * VERSION V2 — Optimisé conversion (prix + étoiles + bénéfices)
   */
  buildAmazonHTML(data) {
    const hasRating = data.rating > 0 && data.rating != null;
    const starsHtml   = hasRating ? this.buildStars(data.rating) : '';
    const scoreHtml   = hasRating ? `<span class="popup-score">${data.rating.toFixed(1)}/5</span>` : '';
    const reviewsHtml = hasRating
      ? `<span class="popup-reviews-count">(${data.reviews || 0} avis)</span>`
      : '';

    const priceHtml = data.price
      ? `<div class="popup-price">Prix : ${this.escapeHtml(data.price)}</div>`
      : '';

    const benefitsHtml = this.buildBenefits(data.description);

    return `
      <button class="popup-close" aria-label="Fermer">&times;</button>

      <h2 class="popup-title">${this.escapeHtml(data.name)}</h2>
      ${priceHtml}

      ${hasRating ? `<div class="popup-rating">${starsHtml} ${scoreHtml} ${reviewsHtml}</div>` : ''}

      ${benefitsHtml}

      <a href="${this.escapeHtml(data.link)}" 
         class="popup-cta" 
         target="_blank" 
         rel="noopener sponsored nofollow">
        Voir ce Produit <span class="popup-arrow">→</span>
      </a>

      <p class="popup-disclaimer">En achetant via ce lien, vous soutenez CocktailConnexion sans surcoût.</p>
    `;
  }

  /* -------------------------------------------------------------------------- */
  /*  OUVERTURE DU POPUP                                                        */
  /* -------------------------------------------------------------------------- */
  openPopup(e, trigger) {
    e.preventDefault();
    e.stopPropagation();

    this.trigger = trigger;
    this.data = this.parseData(trigger);
    const isInt = this.isInternal(this.data);

    if (!isInt && !this.isAmazon(this.data)) {
      console.warn('[Popup] Lien non identifié (ni interne ni Amazon)', this.data);
      return;
    }

    this.currentType = isInt ? 'internal' : 'amazon';
    this.startTime = Date.now();

    if (this.overlay) this.overlay.remove();

    this.overlay = document.createElement('div');
    this.overlay.className = 'popup-overlay';
    this.overlay.addEventListener('click', (ev) => {
      if (ev.target === this.overlay) this.closePopup();
    });

    this.popup = document.createElement('div');
    this.popup.className = `popup-content popup-type-${this.currentType}`;
    this.popup.setAttribute('role', 'dialog');
    this.popup.setAttribute('aria-modal', 'true');

    this.popup.innerHTML = this.currentType === 'internal'
      ? this.buildInternalHTML(this.data)
      : this.buildAmazonHTML(this.data);

    this.overlay.appendChild(this.popup);
    document.body.appendChild(this.overlay);

    /* ---- Fermeture : croix ---- */
    this.closeBtn = this.popup.querySelector('.popup-close');
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        this.closePopup();
      });
    }

    /* ---- Clic CTA : tracking conversion GA4 ---- */
    const ctaBtn = this.popup.querySelector('.popup-cta');
    if (ctaBtn) {
      ctaBtn.addEventListener('click', (ev) => {
        const isInternalBtn = ctaBtn.classList.contains('popup-cta-internal');
        const href = ctaBtn.getAttribute('href');

        const eventName = isInternalBtn ? 'spirit_popup_click' : 'ingredient_popup_click';
        const eventParams = {
          ingredient_name: this.data.name,
          ingredient_id: this.data.id,
          ...(isInternalBtn ? {} : { ingredient_category: this.data.category })
        };

        if (isInternalBtn && href) {
          ev.preventDefault();
          this.pushEvent(eventName, eventParams, () => {
            window.location.href = href;
          });
        } else {
          this.pushEvent(eventName, eventParams);
        }
      });
    }

    // Animation d'entrée
    requestAnimationFrame(() => {
      this.overlay.classList.add('active');
      const focusable = this.popup.querySelector('.popup-cta, .popup-close');
      if (focusable) focusable.focus();
    });

    /* ---- Event GA4 : ouverture ---- */
    this.pushEvent(
      isInt ? 'spirit_popup_open' : 'ingredient_popup_open',
      {
        ingredient_name: this.data.name,
        ingredient_id: this.data.id,
        ...(isInt ? {} : { ingredient_category: this.data.category })
      }
    );
  }

  /* -------------------------------------------------------------------------- */
  /*  FERMETURE DU POPUP                                                        */
  /* -------------------------------------------------------------------------- */
  closePopup() {
    if (!this.overlay) return;

    const duration = this.startTime ? ((Date.now() - this.startTime) / 1000).toFixed(2) : 0;
    const isInt = this.currentType === 'internal';

    this.pushEvent(
      isInt ? 'spirit_popup_close' : 'ingredient_popup_close',
      {
        ingredient_name: this.data.name || '',
        ingredient_id: this.data.id || '',
        duration_seconds: parseFloat(duration),
        ...(isInt ? {} : { ingredient_category: this.data.category || '' })
      }
    );

    this.overlay.classList.remove('active');

    setTimeout(() => {
      if (this.overlay) {
        this.overlay.remove();
        this.overlay = null;
        this.popup = null;
        this.closeBtn = null;
      }
    }, 300);
  }

  /* -------------------------------------------------------------------------- */
  /*  GA4 — Envoi d'événement                                                   */
  /* -------------------------------------------------------------------------- */
  pushEvent(eventName, params, callback = null) {
    const payload = { ...params };

    if (callback && typeof callback === 'function') {
      payload.event_callback = callback;
      payload.transport_type = 'beacon';
    }

    if (typeof gtag === 'function') {
      gtag('event', eventName, payload);

      if (callback) {
        setTimeout(() => {
          if (!callback._called) {
            callback._called = true;
            callback();
          }
        }, 250);
      }
    } else if (typeof dataLayer !== 'undefined' && dataLayer.push) {
      dataLayer.push({ event: eventName, ...payload });
      if (callback) setTimeout(callback, 250);
    } else {
      console.log('[GA4 - mode debug]', eventName, payload);
      if (callback) callback();
    }
  }
}

/* -------------------------------------------------------------------------- */
/*  Démarrage                                                                 */
/* -------------------------------------------------------------------------- */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new IngredientPopupManager());
} else {
  new IngredientPopupManager();
}
